import { useAuth } from "react-oidc-context";

/** Portal roles, in the same string form the identity server / API use. */
export const ROLES = {
  ADMIN: "drawboard-admin",
  DEVS: "projects-devs",
  CLOUD_TEAM: "cloud-team",
} as const;

export const SCOPES = {
  ADMIN: "projects-admin",
  DEVS: "projects-devs",
  CLOUD_TEAM: "cloud-team",
} as const;

/** Human label for a role string. */
export function roleLabel(role: string): string {
  switch (role) {
    case ROLES.ADMIN:     return "Admin";
    case ROLES.DEVS:      return "Devs";
    case ROLES.CLOUD_TEAM:return "Cloud team";
    default:              return role;
  }
}

/** Read the caller's scopes off the OIDC user. The scopes drive UI visibility
 * (the API still re-checks server-side; this is just for rendering). */
export function useScopes(): Set<string> {
  const auth = useAuth();
  const raw = (auth.user?.scopes as string[] | undefined) ?? [];
  return new Set(raw);
}

export function hasScope(scopes: Set<string>, scope: string): boolean {
  return scopes.has(scope);
}

/** Granular permission predicates against the caller's scope set. Mirrors the API auth
 * policies — Services is for devs/cloud-team, Cost + workload actions are cloud-team only,
 * admin user management is admin-only, and granting devs/cloud-team roles is cloud-team only. */
export function usePermissions() {
  const scopes = useScopes();
  return {
    canSeeServices:        scopes.has(SCOPES.DEVS) || scopes.has(SCOPES.CLOUD_TEAM),
    canSeeCost:            scopes.has(SCOPES.CLOUD_TEAM),
    canActOnWorkloads:     scopes.has(SCOPES.CLOUD_TEAM),
    canManageAdmins:       scopes.has(SCOPES.ADMIN),
    canManageOtherRoles:   scopes.has(SCOPES.CLOUD_TEAM),
    canSeeOutages:         scopes.has(SCOPES.ADMIN),
    canManageOutages:      scopes.has(SCOPES.CLOUD_TEAM),
  };
}
